import { FulfillmentType, OrderStatus, type SubOrder } from "@/api/api-client"
import { OrderPipeline } from "@/components/orders/OrderPipeline"
import { getOrderStatusPresentation } from "@/components/orders/order-status-presentation"

import type { VendorOrderLabels } from "./vendor-order-types"

interface VendorOrderPipelineSectionProps {
  subOrder: Pick<SubOrder, "status" | "fulfillmentType">
  title?: string
  statusLabels: VendorOrderLabels["statuses"]
}

function buildVendorDeliveryPipelineSteps(statusLabels: VendorOrderLabels["statuses"]) {
  return [
    { index: 0, label: statusLabels[OrderStatus.Pending] || OrderStatus.Pending },
    { index: 1, label: statusLabels[OrderStatus.StockVerified] || OrderStatus.StockVerified },
    { index: 2, label: statusLabels[OrderStatus.ShippingQuoted] || OrderStatus.ShippingQuoted },
    { index: 3, label: statusLabels[OrderStatus.PaymentReceived] || OrderStatus.PaymentReceived },
    { index: 4, label: statusLabels[OrderStatus.OrderShipped] || OrderStatus.OrderShipped },
  ]
}

function buildVendorPickupPipelineSteps(statusLabels: VendorOrderLabels["statuses"]) {
  return [
    { index: 0, label: statusLabels[OrderStatus.Pending] || OrderStatus.Pending },
    { index: 1, label: statusLabels[OrderStatus.StockVerified] || OrderStatus.StockVerified },
    { index: 2, label: statusLabels[OrderStatus.PaymentReceived] || OrderStatus.PaymentReceived },
    { index: 3, label: statusLabels[OrderStatus.ReadyForPickup] || OrderStatus.ReadyForPickup },
  ]
}

const PICKUP_STEP_INDEX: Record<string, number> = {
  [OrderStatus.Pending]: 0,
  [OrderStatus.StockVerified]: 1,
  [OrderStatus.ShippingQuoted]: 1,
  [OrderStatus.PaymentReceived]: 2,
  [OrderStatus.ReadyForPickup]: 3,
  [OrderStatus.OrderComplete]: 4,
  [OrderStatus.OrderDeclined]: -1,
}

function getVendorOrderPipelineStepIndex(subOrder: Pick<SubOrder, "status" | "fulfillmentType">) {
  const currentStatus = subOrder.status as OrderStatus
  const isPickup = subOrder.fulfillmentType === FulfillmentType.Pickup
  if (isPickup) return PICKUP_STEP_INDEX[currentStatus] ?? 0
  return getOrderStatusPresentation(currentStatus).stepIndex
}

export function VendorOrderPipelineSection({
  subOrder,
  title,
  statusLabels,
}: VendorOrderPipelineSectionProps) {
  const isPickup = subOrder.fulfillmentType === FulfillmentType.Pickup

  return (
    <OrderPipeline
      title={title || "LOGISTICS PROGRESS"}
      steps={
        isPickup
          ? buildVendorPickupPipelineSteps(statusLabels)
          : buildVendorDeliveryPipelineSteps(statusLabels)
      }
      currentStepIndex={getVendorOrderPipelineStepIndex(subOrder)}
    />
  )
}
